/**
 * Authentication Context (Web)
 * Provides authentication state and methods to the React app
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService, AuthUser } from '../services/authService';

interface AuthContextType {
  isAuthenticated: boolean;
  user: AuthUser | null;
  isLoading: boolean;
  approvalStatus: 'pending' | 'approved' | 'rejected' | null;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  refreshAuthState: () => Promise<void>;
  checkApprovalStatus: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [approvalStatus, setApprovalStatus] = useState<'pending' | 'approved' | 'rejected' | null>(null);

  useEffect(() => {
    // Initialize auth service
    const initAuth = async () => {
      try {
        const state = await authService.initialize();
        setIsAuthenticated(state.isAuthenticated);
        setUser(state.user);
        setApprovalStatus(state.approvalStatus);
      } catch (error) {
        // Auth initialization failed
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  const signIn = async () => {
    await authService.signInWithGoogle();
  };

  const signOut = async () => {
    await authService.signOut();
    setIsAuthenticated(false);
    setUser(null);
    setApprovalStatus(null);
  };

  const refreshAuthState = async () => {
    const state = await authService.initialize();
    setIsAuthenticated(state.isAuthenticated);
    setUser(state.user);
    setApprovalStatus(state.approvalStatus);
  };

  const checkApprovalStatus = async () => {
    const result = await authService.checkApprovalStatus();
    if (result) {
      setApprovalStatus(result.approvalStatus);
      if (user) {
        setUser({ ...user, approvalStatus: result.approvalStatus, rejectionReason: result.rejectionReason });
      }
    }
  };

  const value: AuthContextType = {
    isAuthenticated,
    user,
    isLoading,
    approvalStatus,
    signIn,
    signOut,
    refreshAuthState,
    checkApprovalStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
